// Issue #99: maker / series / capacity / thickness / color finder for hubs whose
// collection metafield pnx.series_finder is "capacity". Width hubs keep pnx-series-grid.js.
// The original cards and commerce DOM stay intact; only [data-pnx-capacity-series-filter] is handled.

const PNX_CF_AXES = ['capacity', 'thickness', 'color'];
const PNX_CF_COLOR_ORDER = ['透明', '半透明', '乳白半透明', '黒', '青'];
const PNX_CF_UNKNOWN_CODE = '品番は商品ページで確認';
const PNX_CF_BATCH = 8;

function pnxCfNormalize(raw) {
  return typeof raw === 'string' ? raw.normalize('NFKC').trim() : '';
}

// 「45L」or the maker's range notation 「10〜15L」. 〜 / ～ / ~ are the same separator.
export function pnxCfCapacity(raw) {
  const value = pnxCfNormalize(raw).replace(/[〜～~]/g, '〜');
  const single = value.match(/^(\d+(?:\.\d+)?)\s*L$/i);
  if (single) {
    const size = Number(single[1]);
    return size > 0 && Number.isFinite(size) ? { key: `${size}L`, min: size, max: size, range: 0 } : null;
  }
  const range = value.match(/^(\d+(?:\.\d+)?)\s*(?:L\s*)?〜\s*(\d+(?:\.\d+)?)\s*L$/i);
  if (range) {
    const min = Number(range[1]);
    const max = Number(range[2]);
    return min > 0 && max > min && Number.isFinite(max) ? { key: `${min}〜${max}L`, min, max, range: 1 } : null;
  }
  return null;
}

export function pnxCfThickness(raw) {
  const match = pnxCfNormalize(raw).match(/^(\d+(?:\.\d+)?)\s*mm$/i);
  if (!match) return null;
  const value = Number(match[1]);
  return value > 0 && Number.isFinite(value) ? { key: `${value}mm`, value } : null;
}

export function pnxCfColor(raw) {
  const value = pnxCfNormalize(raw);
  return value && [...value].length <= 40 ? value : null;
}

// OWNER decision (Issue #99 §3-A-1): first word of 「商品名 − シリーズ名」, no whitespace, at least one
// digit, 24 characters or fewer, and not a bare capacity / thickness. Katakana and kanji are allowed.
export function pnxCfCode(raw) {
  const code = pnxCfNormalize(raw);
  if (!code || /\s/.test(code) || !/[0-9]/.test(code) || [...code].length > 24) return null;
  if (pnxCfCapacity(code) || pnxCfThickness(code)) return null;
  return code;
}

function pnxCfProductPath(raw) {
  if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//')) return null;
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin || url.search || url.hash ||
        !/^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?products\/[^/]+\/?$/.test(url.pathname)) return null;
    return url.pathname;
  } catch (_) { return null; }
}

export function pnxCfParseIndex(rawIndex, productPath = pnxCfProductPath) {
  const empty = { records: [], complete: false, unknown: 0 };
  try {
    const data = JSON.parse(rawIndex || '');
    if (data.version !== 2 || !Array.isArray(data.items) || data.items.length > 250 ||
        !Number.isInteger(data.expected) || data.expected < 0 ||
        !Number.isInteger(data.seen) || data.seen !== data.items.length) return empty;
    const records = [];
    const seen = new Set();
    let unknown = 0;
    let duplicate = false;
    data.items.forEach((item) => {
      if (!Array.isArray(item) || item.length !== 6 || item.some((v) => typeof v !== 'string')) {
        unknown += 1; return;
      }
      const [handle, rawURL, rawCode, rawCapacity, rawThickness, rawColor] = item;
      const url = productPath(rawURL);
      if (!handle || !url) { unknown += 1; return; }
      if (seen.has(handle)) { duplicate = true; return; }
      seen.add(handle);
      // Unreadable values stay unknown. Do not infer capacity from dimensions or a code from a SKU.
      records.push({
        handle, url,
        code: pnxCfCode(rawCode) || PNX_CF_UNKNOWN_CODE,
        capacity: pnxCfCapacity(rawCapacity),
        thickness: pnxCfThickness(rawThickness),
        color: pnxCfColor(rawColor)
      });
    });
    return {
      records, unknown,
      complete: data.page === 1 && data.seen === data.expected && !duplicate
    };
  } catch (_) { return empty; }
}

function pnxCfValue(record, axis) {
  const value = record[axis];
  if (value === null || value === undefined) return null;
  return axis === 'color' ? value : value.key;
}

// 'match' | 'missing' | 'no' against the selected record axes, optionally ignoring one axis.
function pnxCfRecordState(record, state, skip) {
  let missing = false;
  for (const axis of PNX_CF_AXES) {
    if (axis === skip || state[axis] === null) continue;
    const value = pnxCfValue(record, axis);
    if (value === null) missing = true;
    else if (value !== state[axis]) return 'no';
  }
  return missing ? 'missing' : 'match';
}

// A result row shows code + color + thickness (+ capacity), so every value must be readable.
function pnxCfRowState(record, state, skip) {
  const result = pnxCfRecordState(record, state, skip);
  if (result !== 'match') return result;
  return PNX_CF_AXES.every((axis) => pnxCfValue(record, axis) !== null) ? 'match' : 'missing';
}

export function pnxCfHasRecordFilter(state) {
  return PNX_CF_AXES.some((axis) => state[axis] !== null);
}

export function pnxCfEvaluate(model, state) {
  const matches = [];
  let missing = 0;
  model.records.forEach((record) => {
    const result = pnxCfRowState(record, state);
    if (result === 'match') matches.push(record);
    else if (result === 'missing') missing += 1;
  });
  return { matches, uncertain: !model.complete || model.unknown > 0 || missing > 0 };
}

function pnxCfModelVisible(model, state) {
  if (!pnxCfHasRecordFilter(state)) return true;
  const { matches, uncertain } = pnxCfEvaluate(model, state);
  return matches.length > 0 || uncertain;
}

export function pnxCfSorters(models) {
  const colorRank = new Map(PNX_CF_COLOR_ORDER.map((color, index) => [color, index]));
  models.forEach((model) => model.records.forEach((record) => {
    if (record.color !== null && !colorRank.has(record.color)) colorRank.set(record.color, colorRank.size);
  }));
  const capacity = (a, b) => a.min - b.min || a.range - b.range || a.max - b.max;
  const thickness = (a, b) => a.value - b.value;
  const color = (a, b) => colorRank.get(a) - colorRank.get(b);
  const code = (a, b) => a.localeCompare(b, 'ja', { numeric: true });
  const records = (a, b) => capacity(a.capacity, b.capacity) || thickness(a.thickness, b.thickness) ||
    color(a.color, b.color) || code(a.code, b.code);
  return { capacity, thickness, color, records };
}

// Options for one record axis: values that keep at least one displayable row with every other
// current condition (maker, series and the other record axes).
export function pnxCfAxisOptions(models, state, axis) {
  const values = new Map();
  models.forEach((model) => {
    if (state.maker !== '*' && model.maker !== state.maker) return;
    if (state.series !== '*' && model.handle !== state.series) return;
    model.records.forEach((record) => {
      if (pnxCfRowState(record, state, axis) !== 'match') return;
      const key = pnxCfValue(record, axis);
      if (!values.has(key)) values.set(key, record[axis]);
    });
  });
  return values;
}

// Maker options ignore the series choice because choosing another maker resets the series.
export function pnxCfMakerOptions(models, state) {
  const makers = new Set();
  models.forEach((model) => {
    if (model.maker && pnxCfModelVisible(model, state)) makers.add(model.maker);
  });
  return makers;
}

export function pnxCfSeriesOptions(models, state) {
  const series = new Set();
  models.forEach((model) => {
    if (state.maker !== '*' && model.maker !== state.maker) return;
    if (pnxCfModelVisible(model, state)) series.add(model.handle);
  });
  return series;
}

function pnxCfElement(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function initCapacitySeriesFilter(filter) {
  if (!(filter instanceof HTMLElement) || filter.dataset.pnxReady === 'capacity-v1') return;
  const section = filter.closest('[id^="shopify-section-"]');
  if (!section) return;
  const cards = [...section.querySelectorAll('.pnx-sgrid__card[data-pnx-series-handle]')];
  const containers = {};
  const rows = {};
  ['maker', 'series', ...PNX_CF_AXES].forEach((axis) => {
    containers[axis] = filter.querySelector(`[data-pnx-cfinder-options="${axis}"]`);
    rows[axis] = filter.querySelector(`[data-pnx-cfinder-row="${axis}"]`);
  });
  const clear = filter.querySelector('[data-pnx-cfinder-clear]');
  const result = filter.querySelector('[data-pnx-cfinder-result]');
  if (!cards.length || Object.values(containers).some((node) => !node) ||
      Object.values(rows).some((node) => !node) || !clear || !result) return;

  const models = cards.map((card) => {
    const index = card.querySelector(':scope > template[data-pnx-product-index]');
    return {
      card,
      handle: card.dataset.pnxSeriesHandle,
      title: card.dataset.pnxSeriesTitle || '',
      maker: card.dataset.pnxMaker || '',
      ...(index ? pnxCfParseIndex(index.dataset.pnxIndex) : { records: [], complete: false, unknown: 0 }),
      wrapper: null,
      panel: null
    };
  }).filter((model) => model.handle);
  if (!models.length) return;

  const sorters = pnxCfSorters(models);
  const makers = [...new Set(models.map((model) => model.maker).filter(Boolean))];
  const series = new Map(models.map((model) => [model.handle, model.title]));
  const seriesMaker = new Map(models.map((model) => [model.handle, model.maker]));
  const allValues = {};
  PNX_CF_AXES.forEach((axis) => {
    const values = new Map();
    models.forEach((model) => model.records.forEach((record) => {
      const key = pnxCfValue(record, axis);
      if (key !== null && !values.has(key)) values.set(key, record[axis]);
    }));
    allValues[axis] = [...values.entries()].sort((a, b) => sorters[axis](a[1], b[1])).map(([key]) => key);
  });
  const state = { maker: '*', series: '*', capacity: null, thickness: null, color: null };
  const buttons = {};
  const selects = {};
  const grids = new Set(models.map((model) => model.card.parentElement));
  const groups = [...section.querySelectorAll('.pnx-sgrid')];
  const labels = { series: 'すべて', capacity: '指定なし', thickness: '指定なし', color: '指定なし' };
  clear.disabled = false; // Keep focus on the same control after resetting.

  function chip(label, kind, value) {
    const button = pnxCfElement('button', `pnx-vfilter__chip pnx-vfilter__chip--${kind} pnx-cfinder__chip--${kind}`, label);
    button.type = 'button';
    button.dataset.pnxFilterValue = value === null ? '*' : String(value);
    return button;
  }

  function conditions() {
    return PNX_CF_AXES.map((axis) => state[axis]).filter((value) => value !== null);
  }

  function restore(model) {
    if (model.wrapper) model.wrapper.replaceWith(model.card);
    model.wrapper = null; model.panel = null;
  }

  function rowSpec(record) {
    const parts = [];
    if (state.capacity === null) parts.push(record.capacity.key);
    parts.push(record.color, record.thickness.key);
    return parts.join('・');
  }

  function resultGroup(records) {
    const group = pnxCfElement('section', 'pnx-vfilter__match-group pnx-cfinder__match-group');
    group.append(pnxCfElement('h4', 'pnx-vfilter__match-heading', `該当する品番：${records.length}件`));
    if (!records.length) {
      group.append(pnxCfElement('p', 'pnx-vfilter__empty', '確認できた該当品番はありません。'));
      return group;
    }
    const list = pnxCfElement('ul', 'pnx-vfilter__products');
    group.append(list);
    let shown = 0;
    function appendBatch() {
      records.slice(shown, shown + PNX_CF_BATCH).forEach((record) => {
        const li = pnxCfElement('li', 'pnx-vfilter__product');
        const link = pnxCfElement('a', 'pnx-vfilter__product-link pnx-cfinder__product-link');
        link.href = record.url;
        link.append(pnxCfElement('strong', 'pnx-vfilter__code', record.code));
        link.append(pnxCfElement('span', 'pnx-cfinder__spec', rowSpec(record)));
        link.append(pnxCfElement('span', 'pnx-vfilter__product-action', '商品を見る →'));
        li.append(link); list.append(li);
      });
      shown = Math.min(shown + PNX_CF_BATCH, records.length);
    }
    appendBatch();
    if (shown < records.length) {
      const more = pnxCfElement('button', 'pnx-vfilter__more');
      more.type = 'button';
      const sync = () => {
        more.textContent = shown < records.length ? `残り${records.length - shown}件を表示` : 'すべて表示しました';
        more.disabled = shown === records.length;
      };
      more.addEventListener('click', () => { appendBatch(); sync(); });
      sync(); group.append(more);
    }
    return group;
  }

  function showProducts(model, matches, uncertain) {
    if (!model.wrapper) {
      model.wrapper = pnxCfElement('div', 'pnx-vfilter__match-row pnx-cfinder__match-row');
      model.card.before(model.wrapper);
      model.wrapper.append(model.card);
      model.panel = pnxCfElement('aside', 'pnx-vfilter__matches pnx-cfinder__matches');
      model.panel.setAttribute('aria-label', `${model.title}の該当品番`);
      model.wrapper.append(model.panel);
    }
    model.panel.replaceChildren();
    model.panel.append(pnxCfElement('h3', 'pnx-vfilter__matches-title', `${conditions().join('・')} の品番を選ぶ`));
    model.panel.append(pnxCfElement('p', 'pnx-vfilter__columns',
      state.capacity === null ? '品番　／　容量・色・厚み' : '品番　／　色・厚み'));
    model.panel.append(resultGroup([...matches].sort(sorters.records)));
    if (uncertain) {
      model.panel.append(pnxCfElement('p', 'pnx-vfilter__incomplete',
        '全商品または一部の容量・厚み・色を確認できていません。この結果だけでは該当なしと判断できません。シリーズ一覧でもご確認ください。'));
    }
    const originalLink = model.card.querySelector('.pnx-sgrid__primary');
    if (originalLink) {
      const link = pnxCfElement('a', 'pnx-vfilter__all-products', 'このシリーズの全商品を見る →');
      link.href = originalLink.href; model.panel.append(link);
    }
  }

  function fillSelect(axis, entries) {
    const select = selects[axis];
    select.replaceChildren();
    entries.forEach(([value, label]) => {
      const option = pnxCfElement('option', '', label);
      option.value = value;
      select.append(option);
    });
    select.value = state[axis] === null ? '*' : state[axis];
  }

  function apply() {
    const filtered = pnxCfHasRecordFilter(state);
    // Maker and series options.
    const makerOptions = pnxCfMakerOptions(models, state);
    const seriesOptions = pnxCfSeriesOptions(models, state);
    buttons.maker.forEach((button, value) => {
      button.setAttribute('aria-pressed', String(value === state.maker));
      button.hidden = value !== '*' && !makerOptions.has(value) && value !== state.maker;
    });
    const seriesVisible = [...series.keys()].filter((handle) => seriesOptions.has(handle) || handle === state.series);
    buttons.series.forEach((button, value) => {
      button.setAttribute('aria-pressed', String(value === state.series));
      button.hidden = value !== '*' && !seriesVisible.includes(value);
    });
    fillSelect('series', [['*', labels.series], ...seriesVisible.map((handle) => [handle, series.get(handle)])]);
    const visibleCount = { maker: makerOptions.size, series: seriesVisible.length };
    // Record axes: values are recalculated from every other current condition.
    PNX_CF_AXES.forEach((axis) => {
      const options = pnxCfAxisOptions(models, state, axis);
      const visible = allValues[axis].filter((key) => options.has(key) || key === state[axis]);
      buttons[axis].forEach((button, value) => {
        button.setAttribute('aria-pressed', String(value === state[axis]));
        button.hidden = value !== null && !visible.includes(value);
      });
      fillSelect(axis, [['*', labels[axis]], ...visible.map((key) => [key, key])]);
      visibleCount[axis] = visible.length;
    });
    // Hide an axis row with one option or fewer, but never drop the visitor's current choice.
    rows.maker.hidden = visibleCount.maker <= 1 && state.maker === '*';
    rows.series.hidden = visibleCount.series <= 1 && state.series === '*';
    PNX_CF_AXES.forEach((axis) => { rows[axis].hidden = visibleCount[axis] <= 1 && state[axis] === null; });

    grids.forEach((grid) => grid.classList.toggle('pnx-vfilter__grid-results', filtered));
    let total = 0; let incomplete = 0;
    const visibleSeries = new Set();
    models.forEach((model) => {
      const eligible = (state.maker === '*' || model.maker === state.maker) &&
        (state.series === '*' || model.handle === state.series);
      const { matches, uncertain } = filtered ? pnxCfEvaluate(model, state) : { matches: [], uncertain: false };
      const show = eligible && (!filtered || state.series !== '*' || matches.length > 0 || uncertain);
      if (filtered && show) showProducts(model, matches, uncertain);
      else restore(model);
      model.card.hidden = !show;
      if (model.wrapper) model.wrapper.hidden = !show;
      if (show) {
        visibleSeries.add(model.handle);
        total += matches.length;
        if (uncertain) incomplete += 1;
      }
    });
    groups.forEach((group) => {
      const own = models.filter((model) => group.contains(model.card));
      if (own.length) group.hidden = own.every((model) => model.card.hidden);
    });
    const context = [];
    if (state.maker !== '*') context.push(state.maker);
    if (state.series !== '*') context.push(series.get(state.series) || state.series);
    context.push(...conditions());
    const prefix = context.length ? `${context.join('・')}：` : '';
    result.textContent = filtered
      ? `${prefix}${total}件（${visibleSeries.size}シリーズ）${incomplete ? ' ※未確認データを含むシリーズあり' : ''}`
      : `${prefix}${visibleSeries.size}シリーズを表示。容量・厚み・色を選ぶと該当品番が表示されます。`;
    clear.setAttribute('aria-disabled', String(state.maker === '*' && state.series === '*' && !filtered));
  }

  const handlers = {
    maker: (maker) => () => {
      state.maker = maker;
      if (state.series !== '*' && maker !== '*' && seriesMaker.get(state.series) !== maker) state.series = '*';
      apply();
    },
    series: (handle) => () => { state.series = handle; apply(); }
  };
  PNX_CF_AXES.forEach((axis) => {
    handlers[axis] = (value) => () => { state[axis] = value === state[axis] ? null : value; apply(); };
  });
  const normalizers = {
    maker: (raw) => raw || null,
    series: (raw) => raw || null,
    capacity: (raw) => pnxCfCapacity(raw)?.key ?? null,
    thickness: (raw) => pnxCfThickness(raw)?.key ?? null,
    color: pnxCfColor
  };
  const fallbackValues = { maker: makers, series: [...series.keys()], ...allValues };

  ['series', ...PNX_CF_AXES].forEach((axis) => {
    const container = containers[axis];
    let select = container.querySelector(':scope > select');
    if (!select) {
      select = pnxCfElement('select', `pnx-vfilter__select pnx-cfinder__select pnx-cfinder__select--${axis}`);
      select.setAttribute('aria-label', container.getAttribute('aria-label') || '');
      container.prepend(select);
    }
    select.addEventListener('change', () => {
      if (axis === 'series') state.series = select.value;
      else state[axis] = select.value === '*' ? null : select.value;
      apply();
    });
    select.disabled = false;
    selects[axis] = select;
  });

  // The section renders every option button (PC wraps them), so adopt those buttons instead of
  // creating new ones: the finder keeps its server geometry when JS starts. A server value that does
  // not normalize, or duplicates another, is hidden (dirty data only). Build buttons only when none exist.
  ['maker', 'series', ...PNX_CF_AXES].forEach((axis) => {
    const all = axis === 'maker' || axis === 'series' ? '*' : null;
    const map = new Map();
    const keys = [];
    containers[axis].querySelectorAll(':scope > .pnx-vfilter__chip').forEach((button) => {
      const raw = button.dataset.pnxFilterValue || '';
      const key = raw === '*' ? all : normalizers[axis](raw);
      if ((raw !== '*' && key === null) || map.has(key)) { button.hidden = true; return; }
      map.set(key, button);
      if (raw !== '*') keys.push(key);
    });
    if (!map.size) {
      [all, ...fallbackValues[axis]].forEach((value) => {
        const label = value === all ? (axis === 'maker' ? 'すべて' : labels[axis])
          : (axis === 'series' ? series.get(value) : value);
        const button = chip(label, axis, value);
        containers[axis].append(button);
        map.set(value, button);
        if (value !== all) keys.push(value);
      });
    }
    map.forEach((button, value) => {
      button.dataset.pnxFilterValue = value === null ? '*' : String(value);
      button.addEventListener('click', handlers[axis](value));
      button.disabled = false;
    });
    buttons[axis] = map;
    if (PNX_CF_AXES.includes(axis)) allValues[axis] = keys; // keep the server order: no re-sort, no DOM move
  });
  clear.addEventListener('click', () => {
    state.maker = '*'; state.series = '*';
    PNX_CF_AXES.forEach((axis) => { state[axis] = null; });
    apply();
    if (window.matchMedia?.('(max-width: 749px)').matches) buttons.maker.get('*')?.focus();
  });
  filter.dataset.pnxReady = 'capacity-v1';
  apply();
}

// Issue #103: on ?page=2+ the server reads each series with the hub's page number, so the card
// indexes come back empty (page 2). Refetch the same section without ?page (Sleek facets.js builds
// its URL the same way) and copy only the index attribute values into the current cards.
// The three pnxSeries* functions are byte-identical in pnx-series-grid.js and pnx-series-finder.js.
function pnxSeriesIndexStale(section) {
  return [...section.querySelectorAll('.pnx-sgrid__card[data-pnx-series-handle]')].some((card) => {
    const index = card.querySelector(':scope > template[data-pnx-product-index]');
    try {
      const page = JSON.parse(index?.getAttribute('data-pnx-index') || '').page;
      return Number.isInteger(page) && page >= 2;
    } catch (_) { return false; }
  });
}

// Resolves to the same section parsed from the page-less Section Rendering response, or null.
// Never rejects; one request, no retry.
function pnxSeriesFetchSection(section) {
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.delete('page');
  url.searchParams.set('section_id', section.id.replace(/^shopify-section-/, ''));
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  return fetch(url.toString(), { credentials: 'same-origin', signal: controller.signal })
    .then((response) => {
      if (!response.ok || !(response.headers.get('content-type') || '').includes('text/html')) return null;
      return response.text().then((html) =>
        new DOMParser().parseFromString(html, 'text/html').getElementById(section.id));
    })
    .catch(() => null)
    .finally(() => clearTimeout(timer));
}

// [[current template, fetched index string], ...] when every card pairs up, or null. Changes nothing.
function pnxSeriesIndexPlan(section, fetched) {
  const read = (root) => {
    const cards = new Map();
    for (const card of root.querySelectorAll('.pnx-sgrid__card[data-pnx-series-handle]')) {
      const key = JSON.stringify([card.getAttribute('data-pnx-series-handle'), card.getAttribute('data-pnx-series-title')]);
      if (cards.has(key)) return null;
      cards.set(key, card.querySelector(':scope > template[data-pnx-product-index]'));
    }
    return cards;
  };
  try {
    const current = read(section);
    const next = fetched ? read(fetched) : null;
    if (!current || !next || !current.size || current.size !== next.size) return null;
    const plan = [];
    for (const [key, template] of current) {
      const source = next.get(key);
      if (!template || !source) return null;
      const value = source.getAttribute('data-pnx-index');
      const data = JSON.parse(value || '');
      const own = JSON.parse(template.getAttribute('data-pnx-index') || '');
      if (!data || !own || data.page !== 1 || data.version !== own.version) return null;
      plan.push([template, value]);
    }
    return plan;
  } catch (_) { return null; }
}

// The capacity / thickness / color buttons are rendered by the server from the same paginate, so a
// stale page also needs the page-less buttons and row visibility. Only <button> chips are imported:
// the fetched <noscript> was parsed with scripting off and its <style> would hide every row.
// [{ options, row, chips, hidden }, ...] for all five axes, or null. Changes nothing.
function pnxCfOptionsPlan(filter, fetched) {
  try {
    const source = fetched?.querySelector('[data-pnx-capacity-series-filter]');
    if (!source) return null;
    const plan = [];
    for (const axis of ['maker', 'series', ...PNX_CF_AXES]) {
      const options = filter.querySelector(`[data-pnx-cfinder-options="${axis}"]`);
      const row = filter.querySelector(`[data-pnx-cfinder-row="${axis}"]`);
      const sourceOptions = source.querySelector(`[data-pnx-cfinder-options="${axis}"]`);
      const sourceRow = source.querySelector(`[data-pnx-cfinder-row="${axis}"]`);
      const chips = sourceOptions ? [...sourceOptions.querySelectorAll(':scope > button.pnx-vfilter__chip')] : [];
      if (!options || !row || !sourceRow || !chips.length) return null;
      plan.push({ options, row, chips, hidden: sourceRow.hidden });
    }
    return plan;
  } catch (_) { return null; }
}

// 1st page: synchronous init as before. Stale index (page 2+): one refetch, then init once either way.
function initCapacitySeriesFilters(scope = document) {
  const filters = [...(scope.querySelectorAll?.('[data-pnx-capacity-series-filter]') || [])];
  if (scope instanceof HTMLElement && scope.matches('[data-pnx-capacity-series-filter]')) filters.push(scope);
  filters.forEach((filter) => {
    const refresh = filter.dataset.pnxIndexRefresh;
    if (refresh === 'pending') return;
    const section = filter.closest('[id^="shopify-section-"]');
    if (refresh === 'done' || refresh === 'failed' || !section || !pnxSeriesIndexStale(section)) {
      initCapacitySeriesFilter(filter);
      return;
    }
    filter.dataset.pnxIndexRefresh = 'pending';
    pnxSeriesFetchSection(section).then((fetched) => {
      const plan = pnxSeriesIndexPlan(section, fetched);
      const options = plan && pnxCfOptionsPlan(filter, fetched);
      if (plan && options) {
        plan.forEach(([template, value]) => template.setAttribute('data-pnx-index', value));
        options.forEach(({ options: current, row, chips, hidden }) => {
          current.querySelectorAll(':scope > button.pnx-vfilter__chip').forEach((button) => button.remove());
          chips.forEach((button) => current.append(document.importNode(button, true)));
          row.hidden = hidden;
        });
      }
      filter.dataset.pnxIndexRefresh = plan && options ? 'done' : 'failed';
      if (filter.isConnected) initCapacitySeriesFilter(filter);
    });
  });
}

if (typeof document !== 'undefined') {
  initCapacitySeriesFilters(document);
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) initCapacitySeriesFilters(event.target);
  });
}
